import { useMemo } from 'react'
import { useQuery, type UseQueryOptions } from '@tanstack/react-query'
import { apiPaths } from '@/api/api-paths'
import { apiRequest } from '@/api/query-client'
import { queryKeys } from '@/api/query-keys'

// Types - V2 API spec
export type Organization = {
  id: string
  name: string
  created_at?: string
  external_id?: string
}

// GET /api/v2/auth/me - v2 backend returns id, preferred_username, email, roles, claims, organizations (no keycloak_sub)
export type UserProfileData = {
  id: string
  keycloak_sub?: string
  preferred_username: string | null
  email: string | null
  roles: string[]
  claims?: Record<string, unknown>
  organizations?: Organization[]
}

export type UserProfileComputed = {
  userInitials: string
  displayName: string
  displayEmail: string
  displayRoles: string[]
  claimsSub: string | null
  claimsEmail: string | null
}

export type UserProfileWithComputed = UserProfileData & UserProfileComputed

type UserProfileResponse = {
  message: string
  data: UserProfileData
}

// Fetch function
async function fetchUserProfile(): Promise<UserProfileData> {
  const res = await apiRequest('GET', apiPaths.auth.me())
  const json: UserProfileResponse = await res.json()
  return json.data
}

// Compute derived values from user profile data
function computeUserProfileValues(userProfile: UserProfileData | undefined): UserProfileComputed {
  let initials = 'U'
  if (userProfile) {
    const fromUsername = userProfile.preferred_username
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
    if (fromUsername) {
      initials = fromUsername
    } else if (userProfile.email?.[0]) {
      initials = userProfile.email[0].toUpperCase()
    }
  }

  return {
    userInitials: initials,
    displayName: userProfile?.preferred_username || userProfile?.email || 'User',
    displayEmail: userProfile?.email || '',
    displayRoles: userProfile?.roles || [],
    claimsSub:
      userProfile?.claims?.sub && typeof userProfile.claims.sub === 'string'
        ? userProfile.claims.sub
        : null,
    claimsEmail:
      userProfile?.claims?.email && typeof userProfile.claims.email === 'string'
        ? userProfile.claims.email
        : null,
  }
}

// Hook
export function useUserProfile(
  options?: Omit<UseQueryOptions<UserProfileData>, 'queryKey' | 'queryFn'>,
) {
  const query = useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchUserProfile,
    ...options,
  })

  const data = useMemo(() => {
    if (!query.data) return undefined

    const computed = computeUserProfileValues(query.data)
    return {
      ...query.data,
      ...computed,
    }
  }, [query.data])

  return {
    ...query,
    data,
  }
}
